import { Form, FormItem } from '@/components/BaseForm';
import Label from '@/components/Label';
import Text from '@/components/Text';
import TumorStageSelect from '@/components/TumorStageSelect';
import { PatientInfoAtom, PatientInfoStatus } from '@/pages/patient/detail';
import RecordImgUpload from '@/pages/patient/detail/components/RecordImgUpload';
import TabHeader, { TabHeaderInput } from '@/pages/patient/detail/components/TabHeader';
import { useInitOcrRes } from '@/pages/patient/hooks/useOcr';
import { addPatientPathology, updatePatientPathology } from '@/services/patient';
import { transformTumorStage } from '@/utils';
import { isPossibleJSON } from '@/utils/jsonValidator';
import { Form as AntdForm, Button, Input } from '@sinohealth/butterfly-ui-components';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

const requiredRule = { required: true, message: '该字段为必填项' };

const SurgicalRecordsTab = (props: any) => {
  const { data } = props;
  const [form] = AntdForm.useForm();
  const [readOnly, setReadOnly] = useState(false);
  const patientInfo = useRecoilValue(PatientInfoAtom);
  const [patientInfoStatus, setPatientInfoStatus] = useRecoilState(PatientInfoStatus);

  useEffect(() => {
    setReadOnly(!!data?.id);
  }, []);

  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    return () => {
      // 组件销毁时，如果是编辑状态，把全局的可编辑状态还原
      if (isEditing) {
        setPatientInfoStatus({
          edit: true,
        });
      }
    };
  }, [isEditing]);

  /** 患者详情图片智能识别结果回填处理 */
  const { handleSecondComfirm, originFormValues, saveLoading, setSaveLoading, recognitTaskId } =
    useInitOcrRes({
      form,
      isEditing: readOnly,
      reportType: 'pathologyReport',
    });

  const handleChangeReadOnly = () => {
    const newReadOnly = !readOnly;
    setReadOnly(newReadOnly);
    setPatientInfoStatus({
      edit: newReadOnly,
    });
    setIsEditing(!newReadOnly);
    if (newReadOnly) {
      patientInfo.update();
    }
    form.resetFields();
  };

  const handleCancel = () => {
    patientInfo.update();
    handleChangeReadOnly();
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((formValue) => {
        const { summary, tumorStaging, immunohistochemistry, ...other } = formValue;
        const params = {
          ...originFormValues,
          ...other,
          diagnosisDescription: JSON.stringify({ summary, tumorStaging, immunohistochemistry }),
          patientId: patientInfo.patient.id,
        };
        setSaveLoading(true);
        const api = params.id ? updatePatientPathology : addPatientPathology;
        if (!formValue?.id) {
          params.recognitionTaskId = recognitTaskId;
        }
        api(params)
          .then(() => {
            patientInfo.update();
            handleChangeReadOnly();
          })
          .finally(() => {
            setSaveLoading(false);
          });
      })
      .catch(() => {});
  };
  const action = readOnly ? (
    <a onClick={handleChangeReadOnly}>
      <Text>编辑</Text>
    </a>
  ) : (
    <>
      <Button
        type="link"
        style={{ padding: 0 }}
        disabled={saveLoading}
        onClick={() => handleSecondComfirm(handleCancel, !data?.id)}
      >
        <Text>取消</Text>
      </Button>
      <Button
        type="link"
        style={{ padding: 0 }}
        disabled={saveLoading}
        onClick={() => handleSecondComfirm(handleSubmit)}
      >
        <Text>保存</Text>
      </Button>
    </>
  );
  const mapImage = (urls: string) => {
    return <RecordImgUpload originUrl={urls} value={urls} disabled />;
  };
  const diagnosisDescription = isPossibleJSON(data?.diagnosisDescription)
    ? JSON.parse(data.diagnosisDescription)
    : {};
  const formValue = {
    ...data,
    ...diagnosisDescription,
  };
  return (
    <Form
      className="record-form"
      requiredMark={false}
      form={form}
      readOnly={readOnly}
      initialValues={formValue}
      layout={readOnly ? 'horizontal' : 'vertical'}
    >
      <FormItem name="id" hidden>
        <Input />
      </FormItem>
      <FormItem
        style={{ padding: 0 }}
        transformText={(text) => (
          <TabHeader
            label={<Text>检查日期</Text>}
            title={text ? moment(text).format('YYYY-MM-DD') : ''}
            action={action}
          />
        )}
        name="recordTime"
        span={24}
        rules={[requiredRule]}
      >
        <TabHeaderInput label={<Text>检查日期</Text>} action={action} />
      </FormItem>
      <FormItem name="imagePath" transformText={mapImage}>
        <RecordImgUpload
          originUrl={data.imagePath}
          fileType="pathologyReport"
          recordId={data?.id}
        />
      </FormItem>
      <FormItem label={<Label>检查所见</Label>} name="checkObservation">
        <Input.TextArea maxLength={1000} showCount autoSize={{ minRows: 4, maxRows: 4 }} />
      </FormItem>
      <FormItem label={<Label>病理诊断</Label>} name="summary">
        <Input.TextArea maxLength={1000} showCount autoSize={{ minRows: 4, maxRows: 4 }} />
      </FormItem>
      <FormItem label={<Label>免疫组化</Label>} name="immunohistochemistry">
        <Input.TextArea autoSize={{ minRows: 4, maxRows: 4 }} maxLength={1000} showCount />
      </FormItem>
      <FormItem
        label={<Label>病理分期</Label>}
        name="tumorStaging"
        transformText={(text) => (text ? transformTumorStage(text) : '--')}
      >
        <TumorStageSelect style={{ width: 325 }} />
      </FormItem>
      <FormItem label={<Label>其他信息</Label>} name="other">
        <Input.TextArea maxLength={1000} showCount autoSize={{ minRows: 4, maxRows: 4 }} />
      </FormItem>
    </Form>
  );
};

export default SurgicalRecordsTab;
